import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "@components/ui/Button";
import { useScrollLock } from "@hooks/useScrollLock";
import styles from "./FormStatusDialog.module.css";

type Variant = "success" | "error";

type Props = {
  open: boolean;
  variant: Variant;
  title: string;
  message: string;
  primaryLabel?: string;
  secondary?: { label: string; href: string; external?: boolean };
  onClose: () => void;
};

const IconCheck = () => (
  <svg viewBox="0 0 24 24" width={32} height={32} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const IconAlert = () => (
  <svg viewBox="0 0 24 24" width={32} height={32} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 9v4M12 17h.01" />
    <path d="M10.3 3.7 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
  </svg>
);

export function FormStatusDialog({
  open,
  variant,
  title,
  message,
  primaryLabel = "Entendido",
  secondary,
  onClose,
}: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    dialogRef.current?.focus({ preventScroll: true });
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const Icon = variant === "success" ? IconCheck : IconAlert;

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden />
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="form-status-title"
        aria-describedby="form-status-desc"
        tabIndex={-1}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Cerrar"
        >
          <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M6 6 L18 18 M6 18 L18 6" />
          </svg>
        </button>

        <div className={`${styles.icon} ${styles[`icon_${variant}`]}`}>
          <Icon />
        </div>

        <h2 id="form-status-title" className={styles.title}>
          {title}
        </h2>
        <p id="form-status-desc" className={styles.message}>
          {message}
        </p>

        <div className={styles.actions}>
          {secondary && (
            <Button
              as="a"
              href={secondary.href}
              variant="secondary"
              {...(secondary.external
                ? { target: "_blank", rel: "noopener" }
                : {})}
              onClick={onClose}
            >
              {secondary.label}
            </Button>
          )}
          <Button
            type="button"
            variant={variant === "success" ? "cta" : "primary"}
            onClick={onClose}
          >
            {primaryLabel}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
